import React, { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { useCreateContentMutation } from '@/store/api/contentApi';
import { useUploadFileMutation } from '@/store/api/uploadApi';
import { contentSchema } from '@/utils/validation';

type ContentForm = z.infer<typeof contentSchema>;
type FormErrors = Partial<Record<keyof ContentForm | 'api', string>>;
type TouchedFields = Partial<Record<keyof ContentForm | 'submitAttempted', boolean>>;

interface UseAddContentFormParams {
  isOpen: boolean;
  onClose: () => void;
  categoryId?: number;
  onSuccess?: () => void;
  isLocked?: boolean;
}

const ALLOWED_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/ogg',
  'audio/aac',
  'audio/x-aac',
  'audio/m4a',
  'audio/x-m4a',
  'audio/mp4',
];

const ALLOWED_AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.aac', '.m4a'];

const getFormErrors = (data: ContentForm): FormErrors => {
  const result = contentSchema.safeParse(data);

  if (result.success) {
    return {};
  }

  const fieldErrors = result.error.flatten().fieldErrors;
  const formattedErrors: FormErrors = {};

  Object.keys(fieldErrors).forEach((key) => {
    const message = fieldErrors[key as keyof ContentForm]?.[0];
    if (message) formattedErrors[key as keyof ContentForm] = message;
  });

  return formattedErrors;
};

const getVisibleFormErrors = (data: ContentForm): FormErrors => {
  const formattedErrors = getFormErrors(data);

  const hasAudioFile = data.file instanceof File;

  const hasVideoFile = !data.isLocked && data.videoFile instanceof File;

  const hasEmbedUrl = Boolean(data.embedUrl?.trim());

  const hasImage = data.image instanceof File;

  // source validation
  if (hasAudioFile || hasVideoFile || hasEmbedUrl) {
    delete formattedErrors.file;
    delete formattedErrors.videoFile; // important
    delete formattedErrors.embedUrl; // optional cleanup
  }

  // image validation
  if (hasImage) {
    delete formattedErrors.image;
  }

  return formattedErrors;
};
const validateField = <K extends keyof ContentForm>(
  key: K,
  value: ContentForm[K],
  currentData: ContentForm
) => {
  const result = contentSchema.safeParse({
    ...currentData,
    [key]: value,
  });

  if (!result.success) {
    const fieldError = result.error.flatten().fieldErrors[key];
    return fieldError?.[0];
  }

  return undefined;
};

export const useAddContentForm = ({
  isOpen,
  onClose,
  categoryId,
  onSuccess,
  isLocked,
}: UseAddContentFormParams) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [image, setImage] = useState<File | undefined>();
  const [file, setFile] = useState<File | undefined>();
  const [videoFile, setVideoFile] = useState<File | undefined>();
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [audioUploadMeta, setAudioUploadMeta] = useState<{
    audioUrl?: string;
    duration?: number;
    audioKey?: string;
    audioUploadToken?: string;
  }>({});
  const [videoUploadMeta, setVideoUploadMeta] = useState<{
    videoUrl?: string;
    videoDuration?: number;
    uploadToken?: string;
  }>({});
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [mantra, setMantra] = useState('');
  const [product, setProduct] = useState('');
  const [personalProduct, setPersonalProduct] = useState('');
  const [mantraTranslation, setMantraTranslation] = useState('');
  const [mantraPronunciation, setMantraPronunciation] = useState('');
  const [mantraIcon, setMantraIcon] = useState<File | undefined>();
  const [mantraIconUrl, setMantraIconUrl] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSourceUploading, setIsSourceUploading] = useState(false);
  const [uploadFile] = useUploadFileMutation();
  const [createContent, { isLoading: isCreating }] = useCreateContentMutation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mantraIconInputRef = useRef<HTMLInputElement>(null);
  const mantraIconUrlRef = useRef<string>();
  const formData = useMemo(
    () => ({
      title,
      description,
      embedUrl,
      file,
      videoFile,
      image,
      isLocked,
      mantra,
      mantraTranslation,
      mantraPronunciation,
      product,
      personalProduct,
      mantraIcon,
    }),
    [
      title,
      description,
      embedUrl,
      file,
      videoFile,
      image,
      isLocked,
      mantra,
      mantraTranslation,
      mantraPronunciation,
      product,
      personalProduct,
      mantraIcon,
    ]
  );

  const getUpdatedFormData = (updates: Partial<ContentForm>): ContentForm => ({
    ...formData,
    ...updates,
  });
  const validationResult = useMemo(() => contentSchema.safeParse(formData), [formData]);

  const stepOneErrors = useMemo(() => {
    const formattedErrors = getVisibleFormErrors(formData);

    delete formattedErrors.mantra;
    delete formattedErrors.mantraTranslation;
    delete formattedErrors.mantraPronunciation;
    delete formattedErrors.product;
    delete formattedErrors.personalProduct;
    delete formattedErrors.mantraIcon;

    return formattedErrors;
  }, [formData]);

  const isFormValid = useMemo(() => {
    const visibleErrors = getVisibleFormErrors(formData);

    return Object.keys(visibleErrors).length === 0;
  }, [formData]);
  const isStepOneValid = Object.keys(stepOneErrors).length === 0;
  const isSourceUploaded =
    (!file || Boolean(audioUploadMeta.audioUploadToken)) &&
    (isLocked || !videoFile || Boolean(videoUploadMeta.uploadToken || videoUploadMeta.videoUrl));
  const isSubmittingAudio = isSourceUploading || isCreating;
  const isLockedFlow = Boolean(isLocked);
  const isMantraStep = isLockedFlow && currentStep === 2;
  const isPrimaryDisabled =
    isLockedFlow && currentStep === 1
      ? isSourceUploading || !isStepOneValid || !isSourceUploaded
      : isSubmittingAudio || isSubmitting || !isFormValid || !isSourceUploaded;

  const resetForm = () => {
    setCurrentStep(1);
    setTitle('');
    setDescription('');
    setEmbedUrl('');
    setFile(undefined);
    setImage(undefined);
    setAudioUploadMeta({});
    if (!isLocked) {
      setVideoFile(undefined);
      setVideoUploadMeta({});
    }
    setImageUrl(undefined);
    setTouched({});
    setMantra('');
    setMantraTranslation('');
    setMantraPronunciation('');
    setMantraIcon(undefined);
    setProduct('');
    setPersonalProduct('');
    setMantraIconUrl(undefined);
    mantraIconUrlRef.current = undefined;
  };

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  const clearImage = () => {
    setImage(undefined);
    setImageUrl(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadAudioInput = async (payload: File | { url: string }) => {
    setIsSourceUploading(true);

    try {
      const uploadResponse = await uploadFile({ file: payload }).unwrap();

      setAudioUploadMeta({
        audioUrl: uploadResponse.meta.url ?? uploadResponse.meta.fileUrl,
        audioKey: uploadResponse.meta.audioKey,
        duration: uploadResponse.meta.duration,
        audioUploadToken: uploadResponse.meta.uploadToken,
      });
    } catch (err) {
      throw err;
    } finally {
      setIsSourceUploading(false);
    }
  };
  const uploadVideoInput = async (payload: File) => {
    setIsSourceUploading(true);

    try {
      const uploadResponse = await uploadFile({
        file: payload,
        kind: 'video',
      }).unwrap();

      setVideoUploadMeta({
        videoUrl:
          (uploadResponse.meta as { videoUrl?: string }).videoUrl ??
          uploadResponse.meta.fileUrl ??
          uploadResponse.meta.url,
        videoDuration: uploadResponse.meta.videoDuration,
        uploadToken: uploadResponse.meta.uploadToken,
      });
    } finally {
      setIsSourceUploading(false);
    }
  };
  const handleAudioSelection = async (selected: File) => {
    setFile(selected);
    setEmbedUrl('');
    setAudioUploadMeta({});
    if (!isLocked) {
      setVideoFile(undefined);
      setVideoUploadMeta({});
    }
    setErrors((prev) => ({
      ...prev,
      ...getVisibleFormErrors({
        title,
        description,
        embedUrl: '',
        file: selected,
        image,
        isLocked,
        mantra,
        mantraTranslation,
        mantraPronunciation,
        mantraIcon,
        product,
        personalProduct,
      }),
      api: undefined,
    }));

    try {
      await uploadAudioInput(selected);
      setErrors((prev) => ({ ...prev, file: undefined, api: undefined }));
    } catch (err: any) {
      const message = err?.data?.message || err?.data?.error || 'Audio upload failed';
      setAudioUploadMeta({});
      setErrors((prev) => ({
        ...prev,
        file: Array.isArray(message) ? message[0] : message,
        api: undefined,
      }));
    }
  };

  const handleVideoSelection = async (selected: File) => {
    if (isLocked) return;
    setVideoFile(selected);
    setFile(undefined);
    setEmbedUrl('');
    setAudioUploadMeta({});

    try {
      await uploadVideoInput(selected);

      setErrors((prev) => ({
        ...prev,
        api: undefined,
        videoFile: undefined,
      }));
    } catch (err: any) {
      const message = err?.data?.message || err?.data?.error || 'Video upload failed';

      setVideoUploadMeta({});

      setErrors((prev) => ({
        ...prev,
        videoFile: Array.isArray(message) ? message[0] : message,
      }));
    }
  };

  console.log('payload icon:', mantraIconUrlRef.current);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setTouched((prev) => ({ ...prev, submitAttempted: true }));
    setIsSubmitting(true);
    const result = contentSchema.safeParse(formData);

    if (!result.success) {
      setErrors(getVisibleFormErrors(formData));
      setIsSubmitting(false);
      return;
    }

    try {
      const trimmedEmbedUrl = embedUrl.trim();

      if (file && !audioUploadMeta.audioUploadToken) {
        setErrors((prev) => ({
          ...prev,
          file: 'Please wait for the audio upload to finish',
        }));
        setIsSubmitting(false);
        return;
      }
      if (!isLocked && videoFile && !videoUploadMeta.uploadToken && !videoUploadMeta.videoUrl) {
        setErrors((prev) => ({
          ...prev,
          videoFile: 'Please wait for video upload',
        }));

        setIsSubmitting(false);

        return;
      }
      await createContent({
        title,
        description,
        categoryId,
        embedUrl: trimmedEmbedUrl || undefined,
        audioUrl: audioUploadMeta.audioUrl,
        videoUrl: !isLocked ? videoUploadMeta.videoUrl : undefined,
        videoDuration: !isLocked ? videoUploadMeta.videoDuration : undefined,
        audioKey: audioUploadMeta.audioKey,
        duration: audioUploadMeta.duration,
        audioUploadToken: audioUploadMeta.audioUploadToken,
        imageUrl,
        ...(isLocked && {
          sanskritMantra: mantra.trim() || undefined,
          englishTranslation: mantraTranslation.trim() || undefined,
          englishPronunciation: mantraPronunciation.trim(),
          productLink: product.trim(),
          personalizedProductLink: personalProduct.trim(),
          iconUrl: mantraIconUrlRef.current,
        }),
      }).unwrap();
      console.log(videoUploadMeta, 'video meta');
      onSuccess?.();
      resetForm();
      setErrors({});
      onClose();
    } catch (err: any) {
      const message = err?.data?.message || err?.data?.error || 'Something went wrong';

      setErrors({ api: Array.isArray(message) ? message[0] : message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setTouched((prev) => ({ ...prev, file: true }));

    const isValidType = !selected.type || ALLOWED_AUDIO_MIME_TYPES.includes(selected.type);
    const isValidExtension = ALLOWED_AUDIO_EXTENSIONS.some((ext) =>
      selected.name.toLowerCase().endsWith(ext)
    );

    if (!isValidType || !isValidExtension) {
      setErrors((prev) => ({
        ...prev,
        file: 'Only MP3, WAV, OGG, AAC, or M4A audio files are allowed',
      }));
      return;
    }

    void handleAudioSelection(selected);
  };
  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (isLocked) return;
    if (!selected) return;

    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg'];

    const isValid = allowedTypes.includes(selected.type);

    if (!isValid) {
      setErrors((prev) => ({
        ...prev,
        videoFile: 'Only MP4, WEBM, or OGG videos are allowed',
      }));

      return;
    }

    void handleVideoSelection(selected);
  };
  const clearSelection = () => {
    setFile(undefined);
    setEmbedUrl('');
    setAudioUploadMeta({});
    if (!isLocked) {
      setVideoFile(undefined);
      setVideoUploadMeta({});
    }
    setErrors((prev) => ({
      ...prev,
      ...getVisibleFormErrors({
        title,
        description,
        embedUrl: '',
        file: undefined,
        videoFile: undefined,
        image,
        isLocked,
        mantra,
        mantraTranslation,
        mantraPronunciation,
        product,
        personalProduct,
        mantraIcon,
      }),
    }));
  };

  const handleNextStep = () => {
    if (!isStepOneValid || !isSourceUploaded) {
      setErrors((prev) => ({
        ...prev,
        ...stepOneErrors,
        ...(file && !audioUploadMeta.audioUploadToken
          ? { file: 'Please wait for the audio upload to finish' }
          : {}),
        ...(!isLocked && videoFile && !videoUploadMeta.uploadToken && !videoUploadMeta.videoUrl
          ? { videoFile: 'Please wait for video upload' }
          : {}),
        api: undefined,
      }));

      return;
    }

    setErrors((prev) => ({
      ...prev,
      api: undefined,
    }));

    setCurrentStep(2);
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);

    const nextFormData = getUpdatedFormData({
      title: value,
    });

    const error = validateField('title', value, nextFormData);

    setErrors((prev) => ({
      ...prev,
      title: error,
      api: undefined,
    }));

    setTouched((prev) => ({
      ...prev,
      title: true,
    }));
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);

    const nextFormData = getUpdatedFormData({
      description: value,
    });

    const error = validateField('description', value, nextFormData);

    setErrors((prev) => ({
      ...prev,
      description: error,
      api: undefined,
    }));

    setTouched((prev) => ({
      ...prev,
      description: true,
    }));
  };

  const handleEmbedUrlChange = (value: string) => {
    setEmbedUrl(value);
    setFile(undefined);
    setAudioUploadMeta({});
    if (!isLocked) {
      setVideoFile(undefined);
      setVideoUploadMeta({});
    }
    const nextFormData = getUpdatedFormData({
      embedUrl: value,
      file: undefined,
      videoFile: undefined,
    });

    const error = validateField('embedUrl', value, nextFormData);

    setErrors((prev) => ({
      ...prev,
      embedUrl: error,
      file: getVisibleFormErrors(nextFormData).file,
      api: undefined,
    }));

    setTouched((prev) => ({
      ...prev,
      embedUrl: true,
    }));
  };

  const handleImageChange = (selected?: File) => {
    if (!selected) return;

    const nextFormData = getUpdatedFormData({
      image: selected,
    });

    setImage(selected);
    setImageUrl(undefined);

    setErrors((prev) => ({
      ...prev,
      image: getFormErrors(nextFormData).image,
      api: undefined,
    }));

    void uploadFile({
      file: selected,
      kind: 'image',
    })
      .unwrap()
      .then((uploadResponse) => {
        const uploadedImageUrl =
          uploadResponse.meta.imageUrl || uploadResponse.meta.url || uploadResponse.meta.fileUrl;

        setImageUrl(uploadedImageUrl);

        setErrors((prev) => ({
          ...prev,
          image: undefined,
          api: undefined,
        }));
      })
      .catch((err: any) => {
        const message = err?.data?.message || err?.data?.error || 'Image upload failed';

        setImageUrl(undefined);

        setErrors((prev) => ({
          ...prev,
          image: Array.isArray(message) ? message[0] : message,
          api: undefined,
        }));
      });
  };

  const handleMantraChange = (value: string) => {
    setMantra(value);

    const nextFormData = {
      ...formData,
      isLocked,
      mantra: value,
    };

    const error = validateField('mantra', value, nextFormData);

    setErrors((prev) => ({
      ...prev,
      mantra: error,
      api: undefined,
    }));

    setTouched((prev) => ({
      ...prev,
      mantra: true,
    }));
  };

  const handleMantraTranslationChange = (value: string) => {
    setMantraTranslation(value);

    const nextFormData = {
      ...formData,
      isLocked,
      mantraTranslation: value,
    };

    const error = validateField('mantraTranslation', value, nextFormData);
    setErrors((prev) => ({
      ...prev,
      mantraTranslation: error,
      api: undefined,
    }));

    setTouched((prev) => ({
      ...prev,
      mantraTranslation: true,
    }));
  };

  const handleMantraPronunciationChange = (value: string) => {
    setMantraPronunciation(value);
    const nextFormData = {
      ...formData,
      isLocked,
      mantraPronunciation: value,
    };

    const error = validateField('mantraPronunciation', value, nextFormData);

    setErrors((prev) => ({
      ...prev,
      mantraPronunciation: error,
      api: undefined,
    }));

    setTouched((prev) => ({
      ...prev,
      mantraPronunciation: true,
    }));
  };

  const handleProductChange = (value: string) => {
    setProduct(value);

    const nextFormData = getUpdatedFormData({
      product: value,
    });

    const error = validateField('product', value, nextFormData);

    setErrors((prev) => ({
      ...prev,
      product: error,
      api: undefined,
    }));

    setTouched((prev) => ({
      ...prev,
      product: true,
    }));
  };

  const handlePersonalProductChange = (value: string) => {
    setPersonalProduct(value);

    const nextFormData = getUpdatedFormData({
      personalProduct: value,
    });

    const error = validateField('personalProduct', value, nextFormData);

    setErrors((prev) => ({
      ...prev,
      personalProduct: error,
      api: undefined,
    }));

    setTouched((prev) => ({
      ...prev,
      personalProduct: true,
    }));
  };
  const handleMantraIconChange = (selected?: File) => {
    if (!selected) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];

    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.svg'];

    const isValidType = !selected.type || allowedTypes.includes(selected.type);

    const isValidExtension = allowedExtensions.some((ext) =>
      selected.name.toLowerCase().endsWith(ext)
    );

    if (!isValidType || !isValidExtension) {
      setErrors((prev) => ({
        ...prev,
        mantraIcon: 'Only PNG, JPG, WEBP, or SVG files are allowed',
      }));

      return;
    }

    setMantraIcon(selected);
    setMantraIconUrl(undefined);

    setTouched((prev) => ({
      ...prev,
      mantraIcon: true,
    }));

    setErrors((prev) => ({
      ...prev,
      mantraIcon: undefined,
    }));

    void uploadFile({
      file: selected,
      kind: 'icon',
    })
      .unwrap()
      .then((res) => {
        const uploadedUrl = res.meta.iconUrl || res.meta.url || res.meta.fileUrl;

        setMantraIconUrl(uploadedUrl);
        mantraIconUrlRef.current = uploadedUrl;
        console.log(res.meta.url);
        setErrors((prev) => ({
          ...prev,
          mantraIcon: undefined,
        }));
      })
      .catch((err: any) => {
        const message = err?.data?.message || err?.data?.error || 'Icon upload failed';

        setMantraIcon(undefined);
        setMantraIconUrl(undefined);

        setErrors((prev) => ({
          ...prev,
          mantraIcon: Array.isArray(message) ? message[0] : message,
        }));
      });
  };

  const handleRemoveMantraIcon = () => {
    setMantraIcon(undefined);
    setMantraIconUrl(undefined);

    setErrors((prev) => ({
      ...prev,
      mantraIcon: getVisibleFormErrors({
        ...formData,
        isLocked,
        mantraIcon: undefined,
      }).mantraIcon,
    }));

    if (mantraIconInputRef.current) {
      mantraIconInputRef.current.value = '';
    }
  };

  const handleSecondaryAction = () => {
    if (isMantraStep) {
      setCurrentStep(1);
      setErrors((prev) => ({ ...prev, api: undefined }));
      return;
    }

    resetForm();
    onClose();
    setErrors({});
  };

  const handlePrimaryAction = () => {
    if (isLockedFlow && currentStep === 1) {
      handleNextStep();
      return;
    }

    void handleSubmit();
  };

  return {
    currentStep,
    title,
    description,
    embedUrl,
    image,
    file,
    videoFile,
    isDragging,
    errors,
    touched,
    mantra,
    mantraTranslation,
    mantraPronunciation,
    product,
    personalProduct,
    mantraIcon,
    isSubmitting,
    isSourceUploading,
    fileInputRef,
    mantraIconInputRef,
    isFormValid,
    isSourceUploaded,
    isSubmittingAudio,
    isLockedFlow,
    isMantraStep,
    isPrimaryDisabled,
    handleTitleChange,
    handleDescriptionChange,
    handleFileChange,
    handleVideoFileChange,
    handleEmbedUrlChange,
    handleImageChange,
    handleMantraChange,
    handleMantraTranslationChange,
    handleMantraPronunciationChange,
    handleProductChange,
    handlePersonalProductChange,
    handleMantraIconChange,
    handleRemoveMantraIcon,
    clearImage,
    clearSelection,
    setIsDragging,
    setTouched,
    setErrors,
    handleAudioSelection,
    handleSecondaryAction,
    handlePrimaryAction,
  };
};
